import { mensagemDeErro } from '@common/errors/mensagemDeErro';
import { formatarDataHora } from '@common/i18n/formatosData';
import {
  Alert,
  AlertDialog,
  Badge,
  Button,
  FormField,
  Input,
  Spinner,
  Tabs,
  Textarea,
  useToast,
} from '@ntconsult/aluma-react';
import { isAxiosError } from 'axios';
import { type ReactNode, type Ref, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBlocker } from 'react-router-dom';
import { useModelosConvite, usePreviaRascunho, useSalvarModelo } from '../hooks';
import { IDIOMAS } from '../hooks/useFiltrosParticipantes';
import type { ModeloConvite, PreviaConvite } from '../types/Convites';
import type { Idioma } from '../types/Painel';
import { PreviaAoVivo } from './PreviaAoVivo';

type Texto = { assunto: string; corpo: string };
type Campo = keyof Texto;
type Variaveis = Record<Campo, string[]>;

const VARIAVEIS = ['{nome}', '{campanha}', '{inicio}', '{fim}'] as const;
const ESPERA_MS = 600;

/** Só pede a prévia sem campo vazio e sem `{` aberta (R11): enquanto se digita `{nom`, nada. */
const pedivel = (m: Texto) =>
  m.assunto.trim() !== '' && m.corpo.trim() !== '' && !/\{[^}]*$/.test(`${m.assunto}\n${m.corpo}`);

/** `variaveis` por campo do `422 VARIAVEL_DESCONHECIDA` (spec 30 R7). */
const variaveisDoErro = (e: unknown): Variaveis | null => {
  const v = isAxiosError(e) && e.response?.status === 422 ? e.response.data?.variaveis : null;
  return v && Array.isArray(v.assunto) && Array.isArray(v.corpo) ? v : null;
};

/** O que a página da campanha usa para o "Continuar" e os envios (R15). */
export type ModelosEmailHandle = {
  temRascunho: () => boolean;
  /** Um `PUT` por idioma com rascunho; `false` se algum falhou (fica na aba dele, com o erro). */
  salvarRascunhos: () => Promise<boolean>;
  descartarRascunhos: () => void;
};

type Props = {
  campanhaId: string;
  /** O link da campanha, abaixo do editor (R10). */
  link?: ReactNode;
  ref?: Ref<ModelosEmailHandle>;
};

/**
 * Vista do texto do convite (spec 36 R9), "Texto do e-mail" (spec 30 R9 a R15): um modelo por idioma com a prévia ao vivo ao
 * lado. Os rascunhos dos três idiomas ficam aqui, não no editor, para não sumirem na troca de aba.
 */
export function ModelosEmailSecao({ campanhaId, link, ref }: Props) {
  const { t } = useTranslation(['convites', 'participantes', 'common', 'erros']);
  const { toast } = useToast();
  const { data, isLoading, isError, error } = useModelosConvite(campanhaId);
  const salvar = useSalvarModelo(campanhaId);

  const [idioma, setIdioma] = useState<Idioma>('PT');
  const [rascunhos, setRascunhos] = useState<Partial<Record<Idioma, Texto>>>({});
  // O texto que a prévia pede, 600 ms depois da última tecla; sem ele, o gravado.
  const [pedidos, setPedidos] = useState<Partial<Record<Idioma, Texto>>>({});
  const [ultimas, setUltimas] = useState<Partial<Record<Idioma, PreviaConvite>>>({});
  const [errosSalvar, setErrosSalvar] = useState<
    Partial<Record<Idioma, { variaveis?: Variaveis; geral?: string }>>
  >({});
  const [restaurando, setRestaurando] = useState(false);
  const espera = useRef<ReturnType<typeof setTimeout>>(undefined);
  const ultimoCampo = useRef<Campo | null>(null);
  const campos = {
    assunto: useRef<HTMLInputElement>(null),
    corpo: useRef<HTMLTextAreaElement>(null),
  };

  const gravado = (i: Idioma): ModeloConvite | undefined => data?.find((m) => m.idioma === i);
  // Só os dois campos: o `PUT` recusa os demais do modelo gravado.
  const texto = (i: Idioma): Texto => {
    const m = rascunhos[i] ?? gravado(i);
    return { assunto: m?.assunto ?? '', corpo: m?.corpo ?? '' };
  };
  const sujo = (i: Idioma) => {
    const r = rascunhos[i];
    const g = gravado(i);
    return !!r && !!g && (r.assunto !== g.assunto || r.corpo !== g.corpo);
  };
  const algumSujo = IDIOMAS.some(sujo);
  // O bloqueio de saída lê o valor da hora (o `useBlocker` guarda a função de um render antigo).
  const sujoAgora = useRef(false);
  sujoAgora.current = algumSujo;

  const atual = texto(idioma);
  const pedido = pedidos[idioma] ?? gravado(idioma) ?? null;
  const previa = usePreviaRascunho(
    campanhaId,
    idioma,
    pedido && pedivel(pedido) ? { assunto: pedido.assunto, corpo: pedido.corpo } : null,
  );
  // A última prévia válida fica enquanto a nova chega ou se ela falha (R11).
  if (previa.data && previa.data !== ultimas[idioma])
    setUltimas((u) => ({ ...u, [idioma]: previa.data }));
  const mostrada = previa.data ?? ultimas[idioma];

  const variaveisRuins = errosSalvar[idioma]?.variaveis ?? variaveisDoErro(previa.error);
  const erroDoCampo = (c: Campo): string | undefined => {
    if (!atual[c].trim()) return t('common:errors.required');
    const ruins = variaveisRuins?.[c];
    return ruins?.length
      ? t('convites:modelo.variavelDesconhecida', { variaveis: ruins.join(', ') })
      : undefined;
  };

  function mudar(campo: Campo, valor: string) {
    const novo = { ...atual, [campo]: valor };
    const i = idioma;
    setRascunhos((r) => ({ ...r, [i]: novo }));
    setErrosSalvar((e) => ({ ...e, [i]: undefined }));
    clearTimeout(espera.current);
    espera.current = setTimeout(() => setPedidos((p) => ({ ...p, [i]: novo })), ESPERA_MS);
  }

  /** R12: na posição do cursor do último campo com foco; sem foco nenhum, no fim do Corpo. */
  function inserir(variavel: string) {
    const campo = ultimoCampo.current ?? 'corpo';
    const el = campos[campo].current;
    const valor = atual[campo];
    const inicio = ultimoCampo.current && el ? (el.selectionStart ?? valor.length) : valor.length;
    const fim = ultimoCampo.current && el ? (el.selectionEnd ?? inicio) : inicio;
    mudar(campo, valor.slice(0, inicio) + variavel + valor.slice(fim));
    const pos = inicio + variavel.length;
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(pos, pos);
    });
  }

  function esquecer(i: Idioma) {
    setRascunhos((r) => ({ ...r, [i]: undefined }));
    setPedidos((p) => ({ ...p, [i]: undefined }));
    setErrosSalvar((e) => ({ ...e, [i]: undefined }));
  }

  /** `PUT` de um idioma; o erro fica no idioma (R14, R15). */
  async function salvarIdioma(i: Idioma) {
    try {
      await salvar.mutateAsync({ idioma: i, modelo: texto(i) });
      return true;
    } catch (e) {
      const variaveis = variaveisDoErro(e);
      setErrosSalvar((x) => ({
        ...x,
        [i]: variaveis ? { variaveis } : { geral: t(`erros:${mensagemDeErro(e)}`) },
      }));
      setIdioma(i);
      return false;
    }
  }

  async function salvarRascunhos() {
    for (const i of IDIOMAS.filter(sujo)) if (!(await salvarIdioma(i))) return false;
    sujoAgora.current = false;
    return true;
  }

  useImperativeHandle(ref, () => ({
    temRascunho: () => sujoAgora.current,
    salvarRascunhos,
    descartarRascunhos: () => {
      for (const i of IDIOMAS) esquecer(i);
      sujoAgora.current = false;
    },
  }));

  // R15: qualquer saída da vista do texto com rascunho pergunta antes (spec 36 R11).
  const bloqueio = useBlocker(
    ({ currentLocation, nextLocation }) =>
      sujoAgora.current &&
      (nextLocation.pathname !== currentLocation.pathname ||
        new URLSearchParams(nextLocation.search).get('secao') !== 'convite'),
  );
  // Fechar ou recarregar a aba: só o navegador pergunta, pelo `beforeunload` (evento externo).
  useEffect(() => {
    if (!algumSujo) return;
    const avisar = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', avisar);
    return () => window.removeEventListener('beforeunload', avisar);
  }, [algumSujo]);

  const modelo = gravado(idioma);
  const vazio = !atual.assunto.trim() || !atual.corpo.trim();
  const geral = errosSalvar[idioma]?.geral;

  return (
    <section className="flex flex-col gap-3">
      <h2 className="text-lg font-semibold text-foreground">{t('convites:modelo.titulo')}</h2>
      {isLoading && <Spinner label={t('common:loading')} />}
      {isError && <Alert variant="danger" title={t(`erros:${mensagemDeErro(error)}`)} />}
      {data && modelo && (
        <div className="@container">
          <div className="flex flex-col gap-6 @min-[1040px]:flex-row @min-[1040px]:items-start">
            <div className="flex min-w-0 flex-1 flex-col gap-3 @min-[1040px]:min-w-[400px]">
              <Tabs value={idioma} onValueChange={(v) => setIdioma(v as Idioma)}>
                <Tabs.List>
                  {IDIOMAS.map((i) => (
                    <Tabs.Trigger key={i} value={i}>
                      <span className="inline-flex items-center gap-1.5">
                        {t(`participantes:idiomas.${i}`)}
                        {sujo(i) && (
                          <span
                            aria-hidden
                            data-rascunho
                            className="size-2 rounded-full bg-warning"
                          />
                        )}
                      </span>
                    </Tabs.Trigger>
                  ))}
                </Tabs.List>
              </Tabs>
              <p className="text-xs text-foreground-muted">
                {modelo.padrao ? (
                  <Badge tone="neutral">{t('convites:modelo.padrao')}</Badge>
                ) : (
                  t('convites:modelo.editadoPor', {
                    nome: modelo.atualizadoPor ?? '—',
                    quando: modelo.atualizadoEm ? formatarDataHora(modelo.atualizadoEm) : '—',
                  })
                )}
              </p>
              <FormField
                label={t('convites:previa.assunto')}
                error={erroDoCampo('assunto')}
                required
              >
                <Input
                  ref={campos.assunto}
                  value={atual.assunto}
                  maxLength={200}
                  onFocus={() => {
                    ultimoCampo.current = 'assunto';
                  }}
                  onChange={(e) => mudar('assunto', e.target.value)}
                />
              </FormField>
              <FormField label={t('convites:modelo.corpo')} error={erroDoCampo('corpo')} required>
                <Textarea
                  ref={campos.corpo}
                  rows={8}
                  maxLength={5000}
                  value={atual.corpo}
                  onFocus={() => {
                    ultimoCampo.current = 'corpo';
                  }}
                  onChange={(e) => mudar('corpo', e.target.value)}
                />
              </FormField>
              <div className="flex flex-wrap gap-2">
                {VARIAVEIS.map((v) => (
                  <Button key={v} variant="secondary" size="sm" onClick={() => inserir(v)}>
                    {v}
                  </Button>
                ))}
              </div>
              {geral && (
                <Alert
                  variant="danger"
                  role="alert"
                  onDismiss={() => setErrosSalvar((e) => ({ ...e, [idioma]: undefined }))}
                >
                  {geral}
                </Alert>
              )}
              <div className="flex flex-wrap gap-2">
                {sujo(idioma) && !vazio && (
                  <Button
                    variant="secondary"
                    loading={salvar.isPending && !restaurando}
                    onClick={async () => {
                      if (await salvarIdioma(idioma))
                        toast.success({ title: t('convites:modelo.salvo') });
                    }}
                  >
                    {t('convites:modelo.salvar')}
                  </Button>
                )}
                {sujo(idioma) && (
                  <Button variant="secondary" onClick={() => esquecer(idioma)}>
                    {t('convites:modelo.descartar')}
                  </Button>
                )}
                {!modelo.padrao && (
                  <Button variant="secondary" onClick={() => setRestaurando(true)}>
                    {t('convites:modelo.restaurar')}
                  </Button>
                )}
              </div>
              {link}
            </div>
            <PreviaAoVivo
              previa={mostrada}
              atualizando={previa.isFetching}
              className="@min-[1040px]:w-[600px] @min-[1040px]:shrink-0"
            />
          </div>
        </div>
      )}

      <AlertDialog open={restaurando} onOpenChange={(o) => !o && setRestaurando(false)}>
        <AlertDialog.Content>
          <AlertDialog.Title>{t('convites:modelo.restaurarTitulo')}</AlertDialog.Title>
          <AlertDialog.Description>{t('convites:modelo.restaurarCorpo')}</AlertDialog.Description>
          <AlertDialog.Footer>
            <AlertDialog.Cancel variant="secondary">{t('common:cancel')}</AlertDialog.Cancel>
            <AlertDialog.Action
              variant="danger"
              loading={salvar.isPending}
              onClick={async () => {
                try {
                  await salvar.mutateAsync({ idioma, modelo: null });
                  esquecer(idioma);
                  toast.success({ title: t('convites:modelo.restaurado') });
                } catch (e) {
                  toast.danger({ title: t(`erros:${mensagemDeErro(e)}`) });
                } finally {
                  setRestaurando(false);
                }
              }}
            >
              {t('convites:modelo.restaurar')}
            </AlertDialog.Action>
          </AlertDialog.Footer>
        </AlertDialog.Content>
      </AlertDialog>

      <AlertDialog
        open={bloqueio.state === 'blocked'}
        onOpenChange={(o) => !o && bloqueio.reset?.()}
      >
        <AlertDialog.Content>
          <AlertDialog.Title>{t('convites:modelo.saidaTitulo')}</AlertDialog.Title>
          <AlertDialog.Footer>
            <AlertDialog.Cancel
              variant="secondary"
              onClick={(e) => {
                // Sem o `setOpen(false)` do Aluma: ele chamaria o `reset` depois do `proceed`.
                e.preventDefault();
                for (const i of IDIOMAS) esquecer(i);
                sujoAgora.current = false;
                bloqueio.proceed?.();
              }}
            >
              {t('convites:modelo.descartar')}
            </AlertDialog.Cancel>
            <AlertDialog.Action
              variant="primary"
              loading={salvar.isPending}
              onClick={async () => {
                bloqueio.reset?.();
                if (await salvarRascunhos()) toast.success({ title: t('convites:modelo.salvo') });
              }}
            >
              {t('convites:modelo.voltarSalvar')}
            </AlertDialog.Action>
          </AlertDialog.Footer>
        </AlertDialog.Content>
      </AlertDialog>
    </section>
  );
}
